import React, { Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';

// Helper to decide how to render lesson content
const renderLessonContent = (lesson) => {
  if (!lesson) return null;

  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';

  // Determine effective type from backend naming variance
  const type = lesson.lesson_type || lesson.type;
  const contentUrl = lesson.content_url || lesson.url || lesson.video_path || '';

  // If full iframe HTML stored (common for embeds)
  if (contentUrl.includes('<iframe')) {
    return (
      <div
        className="w-full h-full"
        dangerouslySetInnerHTML={{ __html: contentUrl }}
      />
    );
  }

  // Uploaded video file (.mp4, .mov, .avi)
  if (
    type === 'video_file' ||
    /(\.mp4$|\.mov$|\.avi$)/i.test(contentUrl)
  ) {
    // If URL is only filename, prepend API path
    const isAbsolute = contentUrl.startsWith('http') || contentUrl.startsWith('/') || contentUrl.includes('uploads/');
    const src = isAbsolute ? `${apiUrl}${contentUrl.startsWith('/') ? '' : '/'}${contentUrl}` : `${apiUrl}/uploads/lessons/${contentUrl}`;
    return (
      <video
        className="w-full h-full" 
        controls 
        controlsList="nodownload"
      >
        <source src={src} />
        متصفحك لا يدعم تشغيل الفيديو
      </video>
    );
  }

  // External video links (YouTube, Vimeo ...)
  if (type === 'video_url' || contentUrl.includes('youtube') || contentUrl.includes('vimeo')) {
    const src = contentUrl.replace('watch?v=', 'embed/');
    return (
      <iframe
        className="w-full h-full"
        src={src}
        title={lesson.title}
        frameBorder="0"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
      />
    );
  }

  // Generic embed link (video_embed) assumed to be iframe src
  if (type === 'video_embed') {
    return (
      <iframe
        className="w-full h-full"
        src={contentUrl}
        title={lesson.title}
        frameBorder="0"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
      />
    );
  }

  // Fallback text
  return (
    <div className="flex items-center justify-center w-full h-full text-gray-700 dark:text-gray-200">
      لا يتوفر محتوى قابل للمعاينة لهذا الدرس
    </div>
  );
};

const LessonPreviewModal = ({ isOpen, onClose, lesson }) => {
  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-5xl p-0 overflow-hidden text-left align-middle transition-all transform bg-white dark:bg-gray-800 shadow-xl rounded-lg">
                {/* Header */}
                <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                  <Dialog.Title className="text-lg font-medium text-gray-900 dark:text-white">
                    {(() => {
                    const lessonTitle = lesson?.title || lesson?.name || lesson?.label || lesson?.original_name || '';
                    return lessonTitle ? `معاينة الدرس: ${lessonTitle}` : 'معاينة الدرس';
                  })()}
                  </Dialog.Title>
                  <button
                    onClick={onClose}
                    className="text-gray-500 hover:text-gray-700 dark:text-gray-300 dark:hover:text-white"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                {/* Content */}
                <div className="aspect-video bg-black">
                  {renderLessonContent(lesson)}
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default LessonPreviewModal;
